import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';

import { ArtistFilter } from './ArtistFilter';
import type { ArtistFiltersType } from './types';

interface ArtistFilterButtonProps {
  selectedFilters: ArtistFiltersType;
  filterPanelIsOpen: boolean;
  onShowFilters: (isOpen: boolean) => void;
  onApplyFilters: (filters: ArtistFiltersType) => void;
}

const ArtistFilterButton = ({
  selectedFilters,
  filterPanelIsOpen,
  onShowFilters,
  onApplyFilters,
}: ArtistFilterButtonProps) => {
  const { t } = useTranslation('roster');
  const areFiltersApplied =
    selectedFilters.labels.length > 0 ||
    selectedFilters.isCrossover ||
    selectedFilters.isOnRoster !== undefined;

  const handleButtonClick = useCallback(() => {
    onShowFilters(true);
  }, [onShowFilters]);

  const handleCloseFilters = useCallback(() => {
    onShowFilters(false);
  }, [onShowFilters]);

  return (
    <>
      {filterPanelIsOpen ? (
        <ArtistFilter
          originalFilters={selectedFilters}
          onClose={handleCloseFilters}
          onApplyFilters={onApplyFilters}
        />
      ) : null}
      <button
        className="relative rounded-sm p-md leading-[0] outline outline-1 duration-300 ease-out bg-emphasis-default outline-emphasis-subtle hover:bg-emphasis-strong hover:outline-emphasis-stronger"
        onClick={handleButtonClick}
        data-testid="artist-filter-button"
        aria-label={t('filters.buttonAriaLabel')}
      >
        <Icon
          type="sliders-simple"
          variant={areFiltersApplied ? 'solid' : 'light'}
          size="md"
        />
        {areFiltersApplied ? (
          <div
            data-testid="artist-filter-button-badge"
            className="absolute -right-1/4 -top-1/4 grid h-lg min-w-lg items-center rounded-full border p-xs text-caption-md bg-primary-default border-emphasis-subtle"
          >
            {selectedFilters.labels.length +
              (selectedFilters.isCrossover ? 1 : 0) +
              (selectedFilters.isOnRoster !== undefined ? 1 : 0)}
          </div>
        ) : null}
      </button>
    </>
  );
};

ArtistFilterButton.displayName = 'ArtistFilterButton';

export { ArtistFilterButton };
